import { ArchitectureComponent } from "@/types/unified-model";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import PDF from "@/assets/images/pdf.png";
import Image from "@/assets/images/image.png";
import Text from "@/assets/images/text.png";
import Git from "@/assets/images/git-icon.png";
import {
  Component,
  Shape,
  UnifiedElement,
  UnifiedResponse,
} from "@/types/diagram";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// GitHub / Bitbucket use exactly two path segments (owner/repo). GitLab also
// supports nested groups (group/subgroup/repo), so its arm allows one or more
// extra segments before the repo.
// Optional trailing `?…` and `#…` are stripped by the alt-arms so that a URL
// like `https://github.com/org/repo?path=/README.md` still validates.
const OPTIONAL_QUERY_FRAGMENT = String.raw`(?:\?[^\s]*)?(?:#[^\s]*)?`;
const GITHUB_BITBUCKET_URL_REGEX = new RegExp(
  String.raw`^https:\/\/(?:www\.)?(?:github\.com|bitbucket\.org)\/[^/\s]+\/[^/\s]+?(?:\.git)?\/?${OPTIONAL_QUERY_FRAGMENT}$`,
  "i",
);
const GITLAB_URL_REGEX = new RegExp(
  String.raw`^https:\/\/(?:www\.)?gitlab\.com\/[^/\s]+(?:\/[^/\s]+)+?(?:\.git)?\/?${OPTIONAL_QUERY_FRAGMENT}$`,
  "i",
);
// BREEZEAI-1148 BUG-02: Azure DevOps URLs come in more shapes than the
// previous regex captured. Real-world variants we now accept:
//   - Canonical:            https://dev.azure.com/<org>/<project>/_git/<repo>
//   - Legacy subdomain:     https://<org>.visualstudio.com/<project>/_git/<repo>
//   - DefaultCollection:    https://dev.azure.com/<org>/DefaultCollection/<project>/_git/<repo>
//   - Auth-prefixed URL:    https://<user>@dev.azure.com/<org>/<project>/_git/<repo>
//   - Query-string / anchor: any of the above followed by ?path=/… or #…
// The path is `<host>(/segment)+/_git/<repo>` — one-or-more segments before
// `_git` is what covers the vanilla + DefaultCollection + any legacy prefix
// forms in a single arm.
const AZURE_DEVOPS_URL_REGEX = new RegExp(
  String.raw`^https:\/\/(?:www\.)?(?:[^@/\s]+@)?(?:dev\.azure\.com|[a-zA-Z0-9-]+\.visualstudio\.com)(?:\/[^/\s]+)+\/_git\/[^/\s?#]+(?:\.git)?\/?${OPTIONAL_QUERY_FRAGMENT}$`,
  "i",
);

export type GitProvider = "github" | "gitlab" | "bitbucket" | "azure_devops";

export const getGitProvider = (url: string): GitProvider | null => {
  const u = url.trim().toLowerCase();
  if (u.includes("github.com")) return "github";
  if (u.includes("gitlab.com")) return "gitlab";
  if (u.includes("bitbucket.org")) return "bitbucket";
  if (u.includes("dev.azure.com") || u.includes("visualstudio.com")) return "azure_devops";
  return null;
};

export const isSupportedGitRepoUrl = (url: string): boolean => {
  const trimmed = url.trim();
  // Case-insensitive HTTPS gate — the regexes below are already `/i`, so
  // keeping this check case-sensitive was the one spot where an uppercase
  // paste ("HTTPS://…") would trip and never reach the regex.
  if (!/^https:\/\//i.test(trimmed)) {
    return false;
  }
  return (
    GITHUB_BITBUCKET_URL_REGEX.test(trimmed) ||
    GITLAB_URL_REGEX.test(trimmed) ||
    AZURE_DEVOPS_URL_REGEX.test(trimmed)
  );
};

// Alias for backwards compatibility if referenced elsewhere
export const isGithubOrBitbucketUrl = isSupportedGitRepoUrl;

export const GIT_REPO_URL_HTTPS_ERROR = "Repository URL must use HTTPS.";

export const GIT_REPO_URL_ERROR =
  "Please enter a valid GitHub, GitLab, Bitbucket, or Azure DevOps repository URL.";

export const getGitRepoUrlError = (url: string): string | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^http:\/\//i.test(trimmed)) {
    return GIT_REPO_URL_HTTPS_ERROR;
  }
  if (!/^https:\/\//i.test(trimmed)) {
    return GIT_REPO_URL_HTTPS_ERROR;
  }
  if (!isSupportedGitRepoUrl(trimmed)) {
    return GIT_REPO_URL_ERROR;
  }
  return null;
};

// Accept only absolute http:// or https:// URLs. Blocks `javascript:`, `data:`,
// `file:`, `vbscript:`, and other schemes that can execute in the browser when
// dropped into an <a href>. Empty strings pass — required-ness is enforced
// separately so this can be used for optional URL fields too.
export const isSafeHttpUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const HTTP_URL_ERROR =
  "Enter a valid URL starting with http:// or https://.";

// Reject the raw HTML delimiters that make stored-XSS payloads possible when
// the value is later injected into markup. Ampersand is included because the
// original ticket (BREEZEAI-1040) explicitly calls it out; if a legitimate
// name like "R&D" ever needs to be supported we can revisit.
const NAME_UNSAFE_HTML_PATTERN = /[<>&]/;

export const containsUnsafeHtml = (value: string): boolean =>
  NAME_UNSAFE_HTML_PATTERN.test(value);

export const NAME_UNSAFE_HTML_ERROR =
  "Name cannot contain <, >, or & characters.";

// SQL statement/comment delimiters characteristic of injection payloads
// (BREEZEAI-1040 QA D4: `'; DROP TABLE components; --`). None of these
// sequences are legitimate in a component name; blocking them client-side
// keeps the payload out of audit logs even though the backend uses
// parameterized queries.
const NAME_UNSAFE_SQL_PATTERN = /;|--|\/\*|\*\//;

export const containsUnsafeSql = (value: string): boolean =>
  NAME_UNSAFE_SQL_PATTERN.test(value);

export const NAME_UNSAFE_SQL_ERROR =
  "Name cannot contain ;, --, /*, or */ sequences.";

export const formatString = (name: string) =>
  name
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\B\w/g, (char) => char.toLowerCase());

export const timeAgo = (date: Date): string => {
  const secondsAgo: number = Math.floor(
    (new Date().getTime() - date.getTime()) / 1000,
  );

  if (secondsAgo <= 0) {
    return "just now";
  }

  const intervals: { unit: string; seconds: number }[] = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
  ];

  for (const interval of intervals) {
    const ago: number = Math.floor(secondsAgo / interval.seconds);

    if (ago >= 1) {
      const unit = ago === 1 ? interval.unit : `${interval.unit}s`;
      return `${ago} ${unit} ago`;
    }
  }

  return "just now";
};

export const getKeysByValue = <T extends Record<string, unknown>>(
  obj: T,
  value: unknown,
): (keyof T)[] => {
  return Object.keys(obj).filter((key) => obj[key] === value) as (keyof T)[];
};

export const isRowValid = (type: string, value: any): boolean => {
  switch (type) {
    case "Persona":
      return !!value?.persona?.trim();
    case "Tasks":
      return !!value?.outcome?.trim();
    case "Scenarios":
      return !!value?.scenario?.trim();
    case "Steps":
      return !!value?.step?.trim();
    case "Actions":
      return !!value?.action?.trim();
    case "Architecture":
      return !!value?.name?.trim();
    default:
      return true;
  }
};

export const flattenArchitecturalSpecs = (
  specs: any,
): ArchitectureComponent[] => {
  const flattenedComponents: ArchitectureComponent[] = [];

  for (const group in specs) {
    const entry = specs[group];

    if (Array.isArray(entry)) {
      // Only process non-empty arrays
      entry.forEach((component) => {
        flattenedComponents.push({
          ...component,
          group,
        });
      });
    } else if (group === "event_queue" && typeof entry === "object") {
      flattenedComponents.push({
        name: "Event Queue",
        group,
        description: entry.description || "",
        topics: Array.isArray(entry.topics)
          ? entry.topics.filter(Boolean).map((t: any) => t.trim())
          : [],
      });
    }
  }

  return flattenedComponents;
};

export const getFileTypeIcon = (fileType: string): string => {
  const fileTypeIconMap: Record<string, string> = {
    git: Git,
    image: Image,
    text: Text,
    txt: Text,
    md: Text,
    markdown: Text,
    "code-derived": Text,
    pdf: PDF,
  };

  return fileTypeIconMap[fileType] || Text;
};

export const getFileTypeIconByFileName = (fileName: string): string => {
  const extensionMap: Record<string, any> = {
    ".txt": Text,
    ".md": Text,
    ".markdown": Text,
    ".git": Git,
    ".pdf": PDF,
    ".png": Image,
    ".jpg": Image,
    ".jpeg": Image,
    ".gif": Image,
    ".svg": Image,
    ".webp": Image,
    ".bmp": Image,
  };

  const lowerTitle = fileName.toLowerCase();
  const matchedKey = Object.keys(extensionMap).find((ext) =>
    lowerTitle.endsWith(ext),
  );
  return matchedKey ? extensionMap[matchedKey] : Text;
};

// Server timestamps arrive UTC but without a timezone marker (e.g.
// "2026-06-22 08:09:51.216402"). `new Date()` on such a string is parsed
// as LOCAL time by the browser, skewing every displayed value by the
// viewer's timezone offset. Append "Z" when no marker is present so the
// instant is anchored to UTC before locale conversion.
export function parseServerUtcMs(value?: string): number {
  if (!value) return NaN;
  const trimmed = value.trim();
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
  const iso = trimmed.replace(" ", "T") + (hasTz ? "" : "Z");
  return new Date(iso).getTime();
}

// Assistant messages can carry ```breezeaijson and ```breezeaicitations fenced
// blocks that drive custom renderers. Those are UI markup rather than prose, so
// anything that consumes a message as plain text — the copy button, the Jira
// ticket draft slice — has to strip them first. Shared deliberately: a drafted
// ticket and its manual-entry fallback must be built from identical text, and two
// private copies of this regex pair would eventually drift.
export function stripBreezeAiFences(text?: string): string {
  if (!text) return "";
  return text
    .replace(/```breezeaijson\s*[\s\S]*?\s*```/g, "")
    .replace(/```breezeaicitations\s*[\s\S]*?\s*```/g, "")
    .trim();
}

export const formatDate = (utcDate: string) => {
  const ms = parseServerUtcMs(utcDate);
  if (!Number.isFinite(ms)) return "Invalid date";
  const date = new Date(ms);
  const now = new Date();

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  const diff = date.getTime() - now.getTime();
  const diffMinutes = Math.ceil(Math.abs(diff) / (1000 * 60));
  const diffHours = Math.ceil(Math.abs(diff) / (1000 * 60 * 60));
  const diffDays = Math.ceil(Math.abs(diff) / (1000 * 60 * 60 * 24));

  if (diff < 0) {
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    else if (diffHours < 24) return `${diffHours} hours ago`;
    else if (diffDays < 30) return `${diffDays} days ago`;
    else return date.toLocaleDateString();
  } else {
    if (diffMinutes < 60) return `in ${diffMinutes} minutes`;
    else if (diffHours < 24) return `in ${diffHours} hours`;
    else if (diffDays < 30) return `in ${diffDays} days`;
    else return date.toLocaleDateString();
  }
};

// Utility to download JSON
export const downloadJSON = (data: any, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const segregateShapesAndComponents = (response: UnifiedResponse[]) => {
  const shapes: Shape[] = [];
  const components: Component[] = [];
  response.forEach((item) => {
    // Convert to Shape if type is "2D" or "3D" or "LAYERS"
    if (item.type === "2D" || item.type === "3D" || item.type === "LAYERS") {
      shapes.push({
        name: item.name,
        type: item.type,
        attachTo: item.attachTo ?? undefined,
        svgFile: item.svgFile ?? "",
        svgContent: item.svgContent ?? "",
        path: item.categoryDetails?.path ?? "",
        version: item.version ?? "",
        description: item?.metadata?.description ?? "",
        tags: item?.tags ?? [],
        _id: item._id,
      });
    }

    // Convert to Component if type is "COMPONENT"
    if (item.type === "COMPONENT") {
      components.push({
        _id: item._id,
        id: item.name,
        name: item.name,
        description: item?.metadata?.description ?? "",
        diagramComponents: item.diagram_components,
        attachmentPoints: item.attachment_points,
        svgContent: item.svgContent ?? "",

        path: item.categoryDetails?.path ?? "",
        version: item.version ?? "",
        created: new Date(item.createdAt),
        lastModified: new Date(item.updatedAt),
        tags: item?.tags ?? [],
      });
    }
  });

  return { shapes, components };
};

export const transformToUnifiedResponse = (
  response: UnifiedResponse[],
): UnifiedElement[] => {
  return response.map((item) => {
    if (item.type === "COMPONENT") {
      return {
        _id: item._id,
        id: item.name,
        name: item.name,
        description: item.metadata?.description ?? "",
        type: "COMPONENT",
        diagramComponents: item.diagram_components,
        attachmentPoints: item.attachment_points,
        svgContent: item.svgContent ?? "",
        path: item.categoryDetails?.path ?? "",
        version: item.version ?? "",
        created: new Date(item.createdAt),
        lastModified: new Date(item.updatedAt),
        status: item?.status,
      };
    }

    return {
      id: item.name,
      name: item.name,
      description: item.metadata?.description ?? "",
      type: item.type as "2D" | "3D" | "LAYERS",
      attachTo: item.attachTo ?? undefined,
      svgFile: item.svgFile ?? "",
      svgContent: item.svgContent ?? "",
      path: item.categoryDetails?.path ?? "",
      version: item.version ?? "",
      status: item.status,
      _id: item._id,
    };
  });
};